import React, { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { Button, Grid, TextField, CircularProgress } from "@mui/material";

import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useAuth } from "hooks/useAuth";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useListListQuery } from "store/services/listService";
import { ENTITY_LIST_CONSTANTS } from "data/static";

const INITIAL_STATE = {
  account: null,
  entity: null,
  vehicleRegistrationNumber: "",
  vehicleType: null,
  baseLocation: "",
  driverName: "",
  driverNumber: "",
  ownerName: "",
  ownerNumber: "",
  payload: null,
  length: null,
  bodyType: null,
  driveType: null,
  isActive: true,
  hasVATBill: false,
};

const VehicleListingForm = ({
  isBusy = false,
  vehicleListingDetail = null,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  // RTK Queries
  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  const {
    data: vehicleTypeList = [],
    isLoading: isLoadingVehicleTypes,
    isFetching: isFetchingVehicleTypes,
  } = useListListQuery(
    { type: ENTITY_LIST_CONSTANTS.EXTERNAL_DRIVER_RECORD_VEHICLE_TYPES, status: "Active" },
    { skip: false, refetchOnMountOrArgChange: true }
  );

  const {
    data: payloadList = [],
    isLoading: isLoadingPayloads,
    isFetching: isFetchingPayloads,
  } = useListListQuery(
    { type: ENTITY_LIST_CONSTANTS.EXTERNAL_DRIVER_RECORD_PAYLOAD, status: "Active" },
    { skip: false, refetchOnMountOrArgChange: true }
  );

  const {
    data: lengthList = [],
    isLoading: isLoadingLengths,
    isFetching: isFetchingLengths,
  } = useListListQuery(
    { type: ENTITY_LIST_CONSTANTS.EXTERNAL_DRIVER_RECORD_LENGTH, status: "Active" },
    { skip: false, refetchOnMountOrArgChange: true }
  );

  const {
    data: bodyTypeList = [],
    isLoading: isLoadingBodyTypes,
    isFetching: isFetchingBodyTypes,
  } = useListListQuery(
    { type: ENTITY_LIST_CONSTANTS.EXTERNAL_DRIVER_RECORD_BODY_TYPES, status: "Active" },
    { skip: false, refetchOnMountOrArgChange: true }
  );

  const {
    data: driveTypeList = [],
    isLoading: isLoadingDriveTypes,
    isFetching: isFetchingDriveTypes,
  } = useListListQuery(
    { type: ENTITY_LIST_CONSTANTS.EXTERNAL_DRIVER_RECORD_DRIVE_TYPES, status: "Active" },
    { skip: false, refetchOnMountOrArgChange: true }
  );

  // React Hook Form
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity || null,
      account: viewEntity?.entity?.account || null,
    },
    mode: "onChange",
  });

  const _watchAccount = watch("account");

  const isListsLoading =
    isLoadingVehicleTypes || isFetchingVehicleTypes ||
    isLoadingPayloads || isFetchingPayloads ||
    isLoadingLengths || isFetchingLengths ||
    isLoadingBodyTypes || isFetchingBodyTypes ||
    isLoadingDriveTypes || isFetchingDriveTypes;

  // Memoize mapped lists
  const _mappedVehicleTypes = useMemo(
    () => vehicleTypeList.map((item) => ({ label: item?.name || "", _id: item?._id || "" })),
    [vehicleTypeList]
  );

  const _mappedPayloads = useMemo(
    () => payloadList.map((item) => ({ label: item?.name || "", _id: item?._id || "" })),
    [payloadList]
  );

  const _mappedLengths = useMemo(
    () => lengthList.map((item) => ({ label: item?.name || "", _id: item?._id || "" })),
    [lengthList]
  );

  const _mappedBodyTypes = useMemo(
    () => bodyTypeList.map((item) => ({ label: item?.name || "", _id: item?._id || "" })),
    [bodyTypeList]
  );

  const _mappedDriveTypes = useMemo(
    () => driveTypeList.map((item) => ({ label: item?.name || "", _id: item?._id || "" })),
    [driveTypeList]
  );

  // Handle Form Submission
  const handleOnSubmit = (data) => {
    onSubmit({
      ...data,
      account: data?.account?._id,
      entity: data?.entity?._id,
      vehicleType: data?.vehicleType?._id,
      payload: data?.payload?._id,
      length: data?.length?._id,
      bodyType: data?.bodyType?._id,
      driveType: data?.driveType?._id,
    });
  };

  // Helper to resolve a list item for reset
  const resolveListItem = (list, detailField) => {
    const found = list.find(
      (item) => item._id === (detailField?._id || detailField)
    );
    return found ? { _id: found._id, label: found.name } : null;
  };

  // Reset form when vehicleListingDetail changes
  const isMounted = useRef(true);
  useEffect(() => {
    if (!isMounted.current || isListsLoading) return;

    if (!vehicleListingDetail) {
      reset({
        ...INITIAL_STATE,
        entity: viewEntity?.entity || null,
        account: viewEntity?.entity?.account || null,
      });
      return;
    }

    reset({
      ...INITIAL_STATE,
      ...vehicleListingDetail,
      account: vehicleListingDetail.account || null,
      entity: vehicleListingDetail.entity || null,
      vehicleType: resolveListItem(vehicleTypeList, vehicleListingDetail.vehicleType),
      payload: resolveListItem(payloadList, vehicleListingDetail.payload),
      length: resolveListItem(lengthList, vehicleListingDetail.length),
      bodyType: resolveListItem(bodyTypeList, vehicleListingDetail.bodyType),
      driveType: resolveListItem(driveTypeList, vehicleListingDetail.driveType),
    });

    return () => {
      isMounted.current = false;
    };
  }, [
    vehicleListingDetail,
    reset,
    viewEntity,
    vehicleTypeList,
    payloadList,
    lengthList,
    bodyTypeList,
    driveTypeList,
    isListsLoading,
  ]);

  // Fetch entities when account changes
  useEffect(() => {
    if ((isAccountAdmin || isSystemAdmin) && _watchAccount?._id && isMounted.current) {
      fetchEntities({ account: _watchAccount._id });
    }
  }, [_watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin]);

  // Reusable autocomplete renderer for List-based fields
  const renderListAutocomplete = ({
    name,
    label,
    options,
    isLoading,
    isFetching,
  }) => (
    <Controller
      name={name}
      control={control}
      rules={{ required: `${label} is required.` }}
      render={({ field: { value, onChange } }) => (
        <BaseAutocomplete
          fullWidth
          required
          label={label}
          value={value ?? null}
          onChange={(_, data) => onChange(data)}
          getOptionLabel={(option) => option?.label ?? ""}
          isOptionEqualToValue={(option, value) => option?._id === value?._id}
          options={options}
          loading={isLoading || isFetching}
          loadingText={`Loading ${label}...`}
          disabled={isLoading || isFetching}
          helperText={errors?.[name]?.message}
          error={!!errors?.[name]}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              required
              error={!!errors?.[name]}
              helperText={errors?.[name]?.message}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {(isLoading || isFetching) && (
                      <CircularProgress color="inherit" size={20} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}
    />
  );

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>

          {/* Account */}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? null}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", null);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Accounts..."
                    disabled={isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}

          {/* Entity */}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? null}
                    onChange={(_, data) => onChange(data)}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}

          {/* Driver Name */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="driverName"
              control={control}
              rules={{ required: "Driver Name is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  label="Driver Name"
                  size="small"
                  error={!!errors.driverName}
                  helperText={errors.driverName?.message}
                />
              )}
            />
          </Grid>

          {/* Driver Number */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="driverNumber"
              control={control}
              rules={{ required: "Driver Number is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  label="Driver Number"
                  size="small"
                  error={!!errors.driverNumber}
                  helperText={errors.driverNumber?.message}
                />
              )}
            />
          </Grid>

          {/* Owner Name */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="ownerName"
              control={control}
              rules={{ required: "Owner Name is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  label="Owner Name"
                  size="small"
                  error={!!errors.ownerName}
                  helperText={errors.ownerName?.message}
                />
              )}
            />
          </Grid>

          {/* Owner Number */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="ownerNumber"
              control={control}
              rules={{ required: "Owner Number is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  label="Owner Number"
                  size="small"
                  error={!!errors.ownerNumber}
                  helperText={errors.ownerNumber?.message}
                />
              )}
            />
          </Grid>

          {/* Vehicle Registration Number */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="vehicleRegistrationNumber"
              control={control}
              rules={{ required: "Vehicle Registration Number is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  label="Vehicle Registration Number"
                  size="small"
                  error={!!errors.vehicleRegistrationNumber}
                  helperText={errors.vehicleRegistrationNumber?.message}
                />
              )}
            />
          </Grid>

          {/* Vehicle Type */}
          <Grid item xs={12} sm={6}>
            {renderListAutocomplete({
              name: "vehicleType",
              label: "Vehicle Type",
              options: _mappedVehicleTypes,
              isLoading: isLoadingVehicleTypes,
              isFetching: isFetchingVehicleTypes,
            })}
          </Grid>

          {/* Base Location */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="baseLocation"
              control={control}
              rules={{ required: "Base Location is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  label="Base Location"
                  size="small"
                  error={!!errors.baseLocation}
                  helperText={errors.baseLocation?.message}
                />
              )}
            />
          </Grid>

          {/* Payload */}
          <Grid item xs={12} sm={6}>
            {renderListAutocomplete({
              name: "payload",
              label: "Payload",
              options: _mappedPayloads,
              isLoading: isLoadingPayloads,
              isFetching: isFetchingPayloads,
            })}
          </Grid>

          {/* Length */}
          <Grid item xs={12} sm={6}>
            {renderListAutocomplete({
              name: "length",
              label: "Length",
              options: _mappedLengths,
              isLoading: isLoadingLengths,
              isFetching: isFetchingLengths,
            })}
          </Grid>

          {/* Body Type */}
          <Grid item xs={12} sm={6}>
            {renderListAutocomplete({
              name: "bodyType",
              label: "Body Type",
              options: _mappedBodyTypes,
              isLoading: isLoadingBodyTypes,
              isFetching: isFetchingBodyTypes,
            })}
          </Grid>

          {/* Drive Type */}
          <Grid item xs={12} sm={6}>
            {renderListAutocomplete({
              name: "driveType",
              label: "Drive Type",
              options: _mappedDriveTypes,
              isLoading: isLoadingDriveTypes,
              isFetching: isFetchingDriveTypes,
            })}
          </Grid>



          {/* Has VAT Bill */}
          <Grid item xs={6}>
            <Controller
              name="hasVATBill"
              control={control}
              render={({ field }) => (
                <AppSwitch {...field} label="Has VAT Bill?" />
              )}
            />
          </Grid>

          {/* Is Active — edit only */}
          {vehicleListingDetail && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}

        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnSubmit)}
          disabled={isBusy || isListsLoading}
        >
          {vehicleListingDetail ? "Update External Driver Record" : "Add External Driver Record"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default VehicleListingForm;